// src/views/superadmin/SuperAdminLayout.jsx
import { useLocation, useNavigate, NavLink, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Suspense, lazy } from "react";
import { Loader } from "../../components/Loader";
import logo from "../../assets/images/logo.png";

// ─── Lazy loading des composants ──────────────────────────────────────
const SADashboard = lazy(() => import("./SADashboard"));
const SAEtablissements = lazy(() => import("./SAEtablissements"));
const SAAnnexes = lazy(() => import("./SAAnnexes"));
const SAUtilisateurs = lazy(() => import("./SAUtilisateurs"));
const SANiveaux = lazy(() => import("./SANiveaux"));
const SAFilieres = lazy(() => import("./SAFilieres"));
const SAAnneesScolaires = lazy(() => import("./SAAnneesScolaires"));
const SAOffresAbonnement = lazy(() => import("./SAOffresAbonnement"));
const SAFinances = lazy(() => import("./SAFinances"));
const SAStandards = lazy(() => import("./SAStandards"));

const SASitewebParametres = lazy(() => import("./SASitewebParametres"));
const SASitewebContact = lazy(() => import("./SASitewebContact"));
const SASitewebFonctionnalites = lazy(() => import("./SASitewebFonctionnalites"));
const SASitewebStatistiques = lazy(() => import("./SASitewebStatistiques"));
const SASitewebTemoignages = lazy(() => import("./SASitewebTemoignages"));
const SASitewebDocumentsLegaux = lazy(() => import("./SASitewebDocumentsLegaux"));
const SASitewebDemandesContact = lazy(() => import("./SASitewebDemandesContact"));

// ─── Titres des pages ──────────────────────────────────────────────────
const PAGE_TITLES = {
  "/super-admin/dashboard": "Vue d'ensemble",
  "/super-admin/etablissements": "Établissements",
  "/super-admin/annexes": "Annexes",
  "/super-admin/utilisateurs": "Utilisateurs",
  "/super-admin/niveaux": "Niveaux scolaires",
  "/super-admin/filieres": "Séries",
  "/super-admin/annees-scolaires": "Années scolaires",
  "/super-admin/standards": "Standards de provisioning",

  "/super-admin/offres-abonnement": "Offres d'abonnement",
  "/super-admin/finances": "Finances",

  "/super-admin/siteweb/parametres": "Paramètres du site",
  "/super-admin/siteweb/contact": "Coordonnées de contact",
  "/super-admin/siteweb/fonctionnalites": "Fonctionnalités",
  "/super-admin/siteweb/statistiques": "Statistiques",
  "/super-admin/siteweb/temoignages": "Témoignages",
  "/super-admin/siteweb/documents-legaux": "Documents légaux",
  "/super-admin/siteweb/demandes-contact": "Demandes de contact",
};

const PAGE_ICONS = {
  "/super-admin/dashboard": "ti-layout-dashboard",
  "/super-admin/etablissements": "ti-building-bank",
  "/super-admin/annexes": "ti-building-community",
  "/super-admin/utilisateurs": "ti-users",
  "/super-admin/niveaux": "ti-stairs",
  "/super-admin/filieres": "ti-route",
  "/super-admin/annees-scolaires": "ti-calendar",
  "/super-admin/standards": "ti-database",

  "/super-admin/offres-abonnement": "ti-package",
  "/super-admin/finances": "ti-cash",

  "/super-admin/siteweb/parametres": "ti-settings",
  "/super-admin/siteweb/contact": "ti-phone",
  "/super-admin/siteweb/fonctionnalites": "ti-list",
  "/super-admin/siteweb/statistiques": "ti-chart-bar",
  "/super-admin/siteweb/temoignages": "ti-message",
  "/super-admin/siteweb/documents-legaux": "ti-file-text",
  "/super-admin/siteweb/demandes-contact": "ti-inbox",
};

const S = {
  header: {
    display: "flex", alignItems: "center", gap: 16,
  },
  h1: {
    margin: 0, fontSize: 22, fontWeight: 700,
    color: "var(--pr)", textTransform: "uppercase", letterSpacing: 1,
    fontFamily: "'Cinzel', 'Times New Roman', serif",
  },
};

// ─── Composant NavItem ──────────────────────────────────────────────
function NavItem({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `ni${isActive ? " active" : ""}`}
      aria-current={({ isActive }) => (isActive ? "page" : undefined)}
    >
      <i className={`ti ${icon}`} aria-hidden="true" />
      {label}
    </NavLink>
  );
}

// ─── Wrapper Lazy ──────────────────────────────────────────────────────
function LazyRoute({ children }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

export default function SuperAdminLayout() {
  const { user, logoutUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const pageTitle = PAGE_TITLES[location.pathname] ?? "Super Administration";
  const pageIcon = PAGE_ICONS[location.pathname] ?? "ti-layout-dashboard";

  const initials = user?.username
    ? user.username.charAt(0).toUpperCase()
    : "SA";

  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        <aside className="sb" role="complementary">
          {/* Logo + Nom de l'application */}
          <div className="sbrand">
            <div className="sbrand-logo-wrapper">
              <img src={logo} alt="Logo" className="sblogo" />
            </div>
            <div className="sbrand-name">
              <span className="sbrand-title">Suivi Scolaire</span>
              <span className="sbrand-subtitle">Super Administration</span>
            </div>
          </div>

          <nav className="snav" role="navigation" aria-label="Navigation principale">
            <ul className="nav flex-column">
              {/* ─── SECTION PRINCIPALE ─────────────────────────────────── */}
              <li className="nav-item">
                <NavItem to="/super-admin/dashboard" icon="ti-layout-dashboard" label="VUE D'ENSEMBLE" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/etablissements" icon="ti-building-bank" label="ÉTABLISSEMENTS" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/annexes" icon="ti-building-community" label="ANNEXES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/niveaux" icon="ti-stairs" label="NIVEAUX" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/filieres" icon="ti-route" label="SÉRIES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/offres-abonnement" icon="ti-package" label="OFFRES D'ABONNEMENT" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/finances" icon="ti-cash" label="FINANCES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/utilisateurs" icon="ti-users" label="UTILISATEURS" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/annees-scolaires" icon="ti-calendar" label="ANNÉES SCOLAIRES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/standards" icon="ti-database" label="STANDARDS" />
              </li>

              {/* ─── SÉPARATEUR ────────────────────────────────────────── */}
              <li
                style={{
                  margin: "12px 15px 8px",
                  borderTop: "0.5px solid rgba(255,255,255,0.2)",
                  listStyle: "none",
                }}
              />

              {/* ─── SECTION SITE WEB ──────────────────────────────────── */}
              <li
                style={{
                  padding: "4px 15px 8px",
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  color: "rgba(255,255,255,0.5)",
                  textTransform: "uppercase",
                  listStyle: "none",
                }}
              >
                Site Web
              </li>

              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/parametres" icon="ti-settings" label="PARAMÈTRES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/contact" icon="ti-phone" label="COORDONNÉES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/fonctionnalites" icon="ti-list" label="FONCTIONNALITÉS" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/statistiques" icon="ti-chart-bar" label="STATISTIQUES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/temoignages" icon="ti-message" label="TÉMOIGNAGES" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/documents-legaux" icon="ti-file-text" label="DOCUMENTS LÉGAUX" />
              </li>
              <li className="nav-item">
                <NavItem to="/super-admin/siteweb/demandes-contact" icon="ti-inbox" label="DEMANDES DE CONTACT" />
              </li>
            </ul>
          </nav>

          {/* Bloc utilisateur */}
          {user && (
            <div className="susr">
              <div className="uav" title={user.username}>{initials}</div>
              <p className="unm" title={user.username}>{user.username}</p>
              <div className="susr-actions">
                <a href="/modifier-compte" className="susr-btn susr-edit">
                  <i className="ti ti-user-edit" aria-hidden="true" />
                  Modifier le profil
                </a>
                <button onClick={() => logoutUser()} className="susr-btn susr-logout">
                  <i className="ti ti-logout" aria-hidden="true" />
                  Déconnexion
                </button>
              </div>
            </div>
          )}
        </aside>

        <main className="main">
          <div className="topbar">
            <div style={S.header}>
              <i
                className={`ti ${pageIcon}`}
                style={{ color: "var(--or)", fontSize: 28 }}
                aria-hidden="true"
              />
              <h1 style={S.h1}>{pageTitle}</h1>
            </div>
            <div className="tbr">
              <button className="ibtn" onClick={() => window.location.reload()}>
                <i className="ti ti-refresh" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="content">
            <Routes>
              <Route index element={<Navigate to="dashboard" replace />} />

              <Route path="dashboard" element={<LazyRoute><SADashboard /></LazyRoute>} />
              <Route path="etablissements" element={<LazyRoute><SAEtablissements /></LazyRoute>} />
              <Route path="annexes" element={<LazyRoute><SAAnnexes /></LazyRoute>} />
              <Route path="utilisateurs" element={<LazyRoute><SAUtilisateurs /></LazyRoute>} />
              <Route path="niveaux" element={<LazyRoute><SANiveaux /></LazyRoute>} />
              <Route path="filieres" element={<LazyRoute><SAFilieres /></LazyRoute>} />
              <Route path="annees-scolaires" element={<LazyRoute><SAAnneesScolaires /></LazyRoute>} />
              <Route path="standards" element={<LazyRoute><SAStandards /></LazyRoute>} />

              <Route path="offres-abonnement" element={<LazyRoute><SAOffresAbonnement /></LazyRoute>} />
              <Route path="finances" element={<LazyRoute><SAFinances /></LazyRoute>} />

              <Route path="siteweb/parametres" element={<LazyRoute><SASitewebParametres /></LazyRoute>} />
              <Route path="siteweb/contact" element={<LazyRoute><SASitewebContact /></LazyRoute>} />
              <Route path="siteweb/fonctionnalites" element={<LazyRoute><SASitewebFonctionnalites /></LazyRoute>} />
              <Route path="siteweb/statistiques" element={<LazyRoute><SASitewebStatistiques /></LazyRoute>} />
              <Route path="siteweb/temoignages" element={<LazyRoute><SASitewebTemoignages /></LazyRoute>} />
              <Route path="siteweb/documents-legaux" element={<LazyRoute><SASitewebDocumentsLegaux /></LazyRoute>} />
              <Route path="siteweb/demandes-contact" element={<LazyRoute><SASitewebDemandesContact /></LazyRoute>} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
}

// ─── CSS (aligné sur AdminLayout — thème Kamalpha) ──────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Cinzel:wght@600;700&display=swap');
@import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* Bleu du logo */
  --pr:    #1B4F80;
  --pr-lt: #2F6DA8;
  --pr-dk: #0E2F52;
  --pr-bg: #E6EEF7;

  /* Or du logo */
  --or:    #C08A2E;
  --or-lt: #E3B75A;
  --or-dk: #8A5A14;

  --bl: #1a6fff; --bl-bg: #e8f1ff; --bl-dk: #0c447c;
  --tl: #0f6e56; --tl-bg: #e1f5ee;
  --am: #ba7517; --am-bg: #faeeda;
  --rd: #dc3545; --rd-dk: #b02a37; --rd-bg: #fcebeb;
  --gn: #3b6d11; --gn-bg: #eaf3de;
  --s:  #ffffff;
  --s2: #f5f6fa;
  --b:  #e8e8ee;
  --b2: #d0d0da;
  --t:  #0f1b2d;
  --m:  #64748b;
  --h:  #9ba5b4;
  --radius-lg: 10px;
  --radius-md: 7px;
}

body {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: var(--s2);
  font-size: 16px;
  color: var(--t);
  margin: 0;
}

.app  { display: flex; min-height: 100vh; }

.sb {
  width: 250px;
  flex-shrink: 0;
  background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
  border-right: 1px solid rgba(192, 138, 46, 0.35);
  box-shadow: 2px 0 8px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  position: fixed; top: 0; left: 0; height: 100vh;
  z-index: 100;
  overflow-y: auto;
  color: white;
}
.main { flex: 1; margin-left: 250px; display: flex; flex-direction: column; min-width: 0; }

.sbrand {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 30px;
  margin-top: 24px;
  padding: 0 15px;
  flex-shrink: 0;
}

.sbrand-logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.sblogo {
  width: 130px;
  height: auto;
  padding: 6px;
  background-color: white;
  border: 1px solid rgba(192, 138, 46, 0.5);
  border-radius: 10px;
  display: block;
}

.sbrand-name {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  margin-top: 14px;
  width: 100%;
}

.sbrand-title {
  font-size: 17px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
  line-height: 1.2;
  text-align: center;
}

.sbrand-subtitle {
  font-size: 11px;
  font-weight: 400;
  color: var(--or-lt);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
}

.snav { padding: 0 12px; flex-shrink: 0; }
.snav ul { list-style: none; }
.nav-item { list-style: none; }

.ni {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 15px; border-radius: 5px; cursor: pointer;
  font-size: 16px; font-weight: 400; color: white; background: none;
  border: none; width: 100%; text-align: left;
  transition: all 0.3s;
  font-family: inherit;
  text-decoration: none; margin-bottom: 2px;
}
.ni:hover { background: rgba(255, 255, 255, 0.10); }
.ni.active {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-weight: 600;
  box-shadow: inset 3px 0 0 var(--or);
}
.ni i { font-size: 20px; width: 20px; flex-shrink: 0; }
.ni.active i { color: var(--or-lt); }

.susr {
  flex-shrink: 0;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: white;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  margin: 24px 12px 12px;
}
.uav {
  width: 64px; height: 64px; border-radius: 50%;
  background-color: var(--pr-dk);
  border: 2px solid var(--or);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 24px;
  user-select: none; text-transform: uppercase;
}
.unm {
  margin: 0; font-weight: 600; font-size: 1.1rem;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px;
}

.susr-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.susr-btn {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}
.susr-btn i { font-size: 16px; flex-shrink: 0; }

.susr-edit {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
}
.susr-edit:hover { background-color: rgba(255, 255, 255, 0.3); }

.susr-logout {
  background-color: var(--rd);
  color: white;
}
.susr-logout:hover { background-color: var(--rd-dk); }

.ibtn { width: 30px; height: 30px; border-radius: 5px; border: 0.5px solid var(--b); background: var(--s); display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--m); font-size: 17px; transition: all .12s; }
.ibtn:hover { background: var(--s2); color: var(--t); }

/* Topbar : filet dégradé bleu → or */
.topbar  {
  padding: 14px 20px;
  display: flex; align-items: center; justify-content: space-between;
  background: var(--s); flex-shrink: 0; position: sticky; top: 0; z-index: 50;
  border-bottom: none;
}
.topbar::after {
  content: "";
  position: absolute; left: 0; right: 0; bottom: 0; height: 3px;
  background: linear-gradient(90deg, var(--pr) 0%, var(--pr-lt) 55%, var(--or) 100%);
}
.tbr     { display: flex; align-items: center; gap: 5px; }

.content { flex: 1; overflow-y: auto; padding: 16px 18px; }

.bprim { display: flex; align-items: center; gap: 5px; padding: 8px 15px; background: var(--pr); color: #fff; border: none; border-radius: 5px; font-size: 15px; cursor: pointer; font-family: inherit; font-weight: 500; white-space: nowrap; transition: opacity .12s; }
.bprim:hover { opacity: .88; }
.bprim.rd { background: var(--rd); }
.bsec  { display: flex; align-items: center; gap: 5px; padding: 8px 13px; background: var(--s); color: var(--m); border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; cursor: pointer; font-family: inherit; transition: all .12s; }
.bsec:hover { background: var(--s2); color: var(--t); }

.pill     { display: inline-block; padding: 4px 9px; border-radius: 9px; font-size: 13px; font-weight: 500; }
.pill.ok  { background: var(--gn-bg); color: var(--gn); }
.pill.warn{ background: var(--am-bg); color: var(--am); }
.pill.err { background: var(--rd-bg); color: var(--rd); }
.pill.info{ background: var(--bl-bg); color: var(--bl-dk); }
.pill.pu  { background: var(--pr-bg); color: var(--pr); }
.pill.neu { background: var(--s2); color: var(--m); }

.card  { background: var(--s); border: 0.5px solid var(--b); border-radius: var(--radius-md); padding: 14px; }
.ch    { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ct    { font-size: 16px; font-weight: 600; color: var(--t); }

.sg    { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 9px; margin-bottom: 14px; }
.sc    { background: var(--s2); border-radius: var(--radius-md); padding: 11px; }
.sclbl { font-size: 14px; color: var(--m); margin-bottom: 4px; display: flex; align-items: center; gap: 3px; }
.sclbl i { font-size: 15px; }
.scval { font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 28px; font-weight: 600; color: var(--t); line-height: 1; }
.sc-accent { border-left: 3px solid var(--pr); }
.sc-accent-bl { border-left: 3px solid var(--bl); }
.sc-accent-gn { border-left: 3px solid var(--gn); }
.sc-accent-am { border-left: 3px solid var(--am); }

.tbl   { width: 100%; border-collapse: collapse; font-size: 15px; table-layout: fixed; }
.tbl th { text-align: left; padding: 9px 10px; color: var(--m); font-weight: 600; border-bottom: 0.5px solid var(--b); font-size: 14px; white-space: nowrap; }
.tbl td { padding: 10px 10px; border-bottom: 0.5px solid var(--b); color: var(--t); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tbl tr:last-child td { border-bottom: none; }
.tbl tr:hover td { background: var(--s2); }

.act { display: flex; gap: 3px; }
.act button { width: 26px; height: 26px; border-radius: 4px; border: 0.5px solid var(--b); background: none; cursor: pointer; color: var(--m); display: flex; align-items: center; justify-content: center; font-size: 15px; transition: all .12s; }
.act button:hover     { background: var(--s2); color: var(--t); border-color: var(--b2); }
.act button.del:hover { background: var(--rd-bg); color: var(--rd); border-color: var(--rd); }
.act button.ok:hover  { background: var(--gn-bg); color: var(--gn); border-color: var(--gn); }
.act button.warn:hover{ background: var(--am-bg); color: var(--am); border-color: var(--am); }

.av  { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.av.bl { background: var(--bl-bg); color: var(--bl-dk); }
.av.tl { background: var(--tl-bg); color: var(--tl); }
.av.pu { background: var(--pr-bg); color: var(--pr); }
.av.am { background: var(--am-bg); color: var(--am); }
.avc { display: flex; align-items: center; gap: 5px; }

.sb2 { display: flex; align-items: center; gap: 6px; margin-bottom: 11px; flex-wrap: wrap; }
.sb2 input  { flex: 1; min-width: 120px; padding: 8px 13px; border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; background: var(--s); color: var(--t); font-family: inherit; }
.sb2 select { padding: 8px 13px; border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; background: var(--s); color: var(--t); font-family: inherit; }

.row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; margin-bottom: 12px; }

.fg          { display: flex; flex-direction: column; gap: 3px; }
.fg label    { font-size: 14px; color: var(--m); font-weight: 500; }
.fg input, .fg select, .fg textarea { padding: 9px 13px; border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; background: var(--s); color: var(--t); font-family: inherit; width: 100%; }
.fg textarea { resize: vertical; min-height: 56px; }
.fgrid       { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }

.factions { display: flex; gap: 7px; margin-top: 12px; justify-content: flex-end; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px; }
.modal-box     { background: var(--s); border-radius: var(--radius-lg); border: 0.5px solid var(--b); padding: 18px; width: 450px; max-width: 100%; max-height: 90vh; overflow-y: auto; }
.modal-title   { font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 17px; font-weight: 600; color: var(--t); margin-bottom: 13px; display: flex; align-items: center; justify-content: space-between; }

.etab-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 11px; margin-bottom: 12px; }
.etab-card { background: var(--s); border: 0.5px solid var(--b); border-radius: var(--radius-md); padding: 14px; cursor: pointer; transition: box-shadow .15s, border-color .15s; }
.etab-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,.07); border-color: var(--b2); }
.etab-card.inactive { opacity: .65; }
.etab-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 10px; }
.etab-sigle { font-size: 20px; font-weight: 600; color: var(--pr); line-height: 1; }
.etab-nom { font-size: 15px; color: var(--m); margin-top: 2px; }
.etab-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 6px; margin-top: 10px; border-top: 0.5px solid var(--b); padding-top: 10px; }
.etab-stat-val { font-size: 16px; font-weight: 600; color: var(--t); }
.etab-stat-lbl { font-size: 12px; color: var(--m); margin-top: 1px; }

.tabs { display: flex; gap: 3px; margin-bottom: 12px; border-bottom: 0.5px solid var(--b); }
.tab { padding: 6px 14px; font-size: 15px; font-weight: 500; color: var(--m); cursor: pointer; border: none; background: none; border-bottom: 2px solid transparent; margin-bottom: -0.5px; transition: all .12s; font-family: inherit; }
.tab.active { color: var(--pr); border-bottom-color: var(--pr); }
.tab:hover { color: var(--t); }

.loader-container { display: flex; justify-content: center; align-items: center; min-height: 300px; width: 100%; }
.loader-spinner { width: 48px; height: 48px; border: 4px solid var(--s2); border-top: 4px solid var(--pr); border-radius: 50%; animation: spin 0.8s linear infinite; }

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.content-full {
  padding: 0;
  overflow: hidden;
}

::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--s2); }
::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: var(--m); }
`;